import { useMemo } from 'react';
import {
  Hammer, X, Search, Move, RotateCcw, RotateCw, Minus, Plus, Copy, Trash2, Check, Maximize2,
} from 'lucide-react';
import { useWorldBuilderStore } from '../../../store/worldBuilderStore';
import { getByCategory, getAsset } from '../../../assets/thornhavenAssetRegistry';
import { NATIVE_ASSETS, NATIVE_SUBCATEGORIES } from '../environment/prefabs/nativeRegistry';
import AssetThumb from './AssetThumb';
import NativeThumb from './NativeThumb';

const prettyName = (name) => (name || '').replace(/_/g, ' ');

const CC0_SUBS = [
  { key: 'buildings', label: 'Buildings' },
  { key: 'nature', label: 'Nature' },
  { key: 'walls-gates', label: 'Walls & Structures' },
  { key: 'props', label: 'Props' },
  { key: 'furniture', label: 'Furniture' },
  { key: 'misc', label: 'Misc' },
];
const FURNITURE_RE = /(bed|chair|table|bench|stool|chest|shelf|barrel|crate|cabinet|couch|bookcase|lamp|candle|pot|bucket|sack|basket|anvil|cauldron|torch|lantern|bottle|jug|mug|plate|bowl|keg|book|chandelier|throne|desk)/i;
function cc0Sub(a) {
  if (a.category === 'buildings') return 'buildings';
  if (a.category === 'nature') return 'nature';
  if (a.category === 'walls-gates') return 'walls-gates';
  if (a.category === 'props') return FURNITURE_RE.test(a.name) ? 'furniture' : 'props';
  return 'misc';
}
const ALL_PLUGINS = ['buildings', 'nature', 'walls-gates', 'props'].flatMap((c) => getByCategory(c));

export default function WorldBuilderPanel() {
  const builderEnabled = useWorldBuilderStore((s) => s.builderEnabled);
  const toggleBuilder = useWorldBuilderStore((s) => s.toggleBuilder);
  const topTab = useWorldBuilderStore((s) => s.topTab);
  const setTopTab = useWorldBuilderStore((s) => s.setTopTab);
  const activeCategory = useWorldBuilderStore((s) => s.activeCategory);
  const setCategory = useWorldBuilderStore((s) => s.setCategory);
  const search = useWorldBuilderStore((s) => s.search);
  const setSearch = useWorldBuilderStore((s) => s.setSearch);
  const selectedAssetId = useWorldBuilderStore((s) => s.selectedAssetId);
  const selectAsset = useWorldBuilderStore((s) => s.selectAsset);
  const clearAsset = useWorldBuilderStore((s) => s.clearAsset);

  const selectedObjectId = useWorldBuilderStore((s) => s.selectedObjectId);
  const draft = useWorldBuilderStore((s) => s.draft);
  const moveMode = useWorldBuilderStore((s) => s.moveMode);
  const toggleMoveMode = useWorldBuilderStore((s) => s.toggleMoveMode);
  const rotateSelected = useWorldBuilderStore((s) => s.rotateSelected);
  const scaleSelected = useWorldBuilderStore((s) => s.scaleSelected);
  const duplicateSelected = useWorldBuilderStore((s) => s.duplicateSelected);
  const deleteSelected = useWorldBuilderStore((s) => s.deleteSelected);
  const saveSelected = useWorldBuilderStore((s) => s.saveSelected);
  const cancelSelected = useWorldBuilderStore((s) => s.cancelSelected);

  const isMidgard = topTab === 'midgard';
  const subs = isMidgard
    ? NATIVE_SUBCATEGORIES.map((s) => ({ key: s, label: s }))
    : CC0_SUBS;

  const q = search.trim().toLowerCase();
  const match = (name, id) => !q || name.toLowerCase().includes(q) || id.toLowerCase().includes(q);

  const items = useMemo(() => {
    if (isMidgard) {
      return NATIVE_ASSETS.filter((a) => a.sub === activeCategory && match(a.name, a.id)).map((a) => ({
        id: a.id, name: a.name, tier: a.tier, native: true,
      }));
    }
    return ALL_PLUGINS.filter((a) => cc0Sub(a) === activeCategory && match(a.name, a.id)).map((a) => ({
      id: a.id, name: a.name, native: false, pluginName: a.pluginName,
    }));
  }, [isMidgard, activeCategory, search]);

  const selectedAsset = selectedAssetId ? getAsset(selectedAssetId) : null;
  const selectedName = selectedAsset ? selectedAsset.name
    : (NATIVE_ASSETS.find((a) => a.id === selectedAssetId)?.name || 'asset');

  if (!builderEnabled) {
    return (
      <button
        onClick={toggleBuilder}
        className="fixed bottom-28 left-4 z-[70] flex items-center gap-2 px-5 py-4 rounded-2xl bg-amber-600 hover:bg-amber-500 text-white font-bold shadow-xl border border-amber-300/40 pointer-events-auto"
        style={{ touchAction: 'manipulation' }}
        data-testid="world-builder-toggle-btn"
      >
        <Hammer className="w-6 h-6" />
        <span className="text-sm tracking-wide">WORLD BUILDER</span>
      </button>
    );
  }

  const bigBtn = 'flex flex-col items-center justify-center gap-1 rounded-xl py-3 font-semibold text-xs select-none pointer-events-auto transition-colors';

  return (
    <div
      className="fixed top-0 left-0 z-[70] h-full w-[92vw] max-w-[410px] flex flex-col bg-stone-950/95 backdrop-blur-md border-r border-amber-900/40 pointer-events-auto"
      style={{ touchAction: 'manipulation' }}
      data-testid="world-builder-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-amber-900/40">
        <div className="flex items-center gap-2 text-amber-300">
          <Hammer className="w-5 h-5" />
          <span className="font-bold tracking-wide">World Builder</span>
        </div>
        <button onClick={toggleBuilder} className="p-3 rounded-xl bg-stone-800 hover:bg-stone-700 text-stone-200" data-testid="world-builder-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Top-level tabs: MIDGARD vs CC0 PLUGINS */}
      <div className="grid grid-cols-2 gap-2 p-3 pb-1">
        <button
          onClick={() => setTopTab('midgard')}
          className={`py-3 rounded-xl text-sm font-bold tracking-wide transition-colors ${isMidgard ? 'bg-amber-600 text-white' : 'bg-stone-800 text-stone-300'}`}
          data-testid="wb-top-midgard"
        >
          MIDGARD
        </button>
        <button
          onClick={() => setTopTab('plugins')}
          className={`py-3 rounded-xl text-sm font-bold tracking-wide transition-colors ${!isMidgard ? 'bg-sky-700 text-white' : 'bg-stone-800 text-stone-300'}`}
          data-testid="wb-top-plugins"
        >
          CC0 PLUGINS
        </button>
      </div>

      {/* Sub-category pills */}
      <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
        {subs.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            className={`whitespace-nowrap px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${activeCategory === c.key ? (isMidgard ? 'bg-amber-700 text-white' : 'bg-sky-800 text-white') : 'bg-stone-800 text-stone-300'}`}
            data-testid={`wb-sub-${c.key}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2 bg-stone-800 rounded-xl px-3">
          <Search className="w-5 h-5 text-stone-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assets..."
            className="flex-1 bg-transparent py-3 text-stone-100 placeholder-stone-500 outline-none text-sm"
            data-testid="wb-search-input"
          />
          {search && <button onClick={() => setSearch('')} className="text-stone-400 p-1"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Placement banner */}
      {selectedAssetId && (
        <div className="mx-3 mb-2 px-3 py-2 rounded-xl bg-amber-600/20 border border-amber-500/50 flex items-center justify-between">
          <span className="text-amber-100 text-xs">Placing <b>{prettyName(selectedName)}</b> — tap the ground</span>
          <button onClick={clearAsset} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold" data-testid="wb-placement-done-btn">Done</button>
        </div>
      )}

      {/* Asset grid */}
      <div className="flex-1 overflow-y-auto px-3 pb-3" data-testid="wb-asset-grid">
        <div className="grid grid-cols-3 gap-2">
          {items.map((a) => (
            <button
              key={a.id}
              onClick={() => selectAsset(a.id)}
              className={`relative p-1.5 rounded-xl border text-left transition-colors ${selectedAssetId === a.id ? 'border-amber-400 bg-amber-600/20' : a.native ? 'border-emerald-800/50 bg-stone-900 hover:bg-stone-800' : 'border-stone-700 bg-stone-900 hover:bg-stone-800'}`}
              data-testid={`wb-asset-${a.id}`}
              title={a.pluginName || a.tier}
            >
              {a.native ? <NativeThumb assetId={a.id} /> : <AssetThumb assetId={a.id} />}
              {a.native && a.tier !== 'RUSTIC' && (
                <span className={`absolute top-2 right-2 text-[8px] font-bold px-1 rounded ${a.tier === 'ASGARD' ? 'bg-amber-500 text-black' : 'bg-sky-600 text-white'}`}>{a.tier}</span>
              )}
              <div className="mt-1 text-[10px] leading-tight text-stone-300 line-clamp-2 capitalize">{prettyName(a.name)}</div>
            </button>
          ))}
          {items.length === 0 && <div className="col-span-3 py-8 text-center text-stone-500 text-sm">No assets here{q ? ` for "${search}"` : ''}</div>}
        </div>
      </div>

      {/* Transform toolbar */}
      {selectedObjectId && draft && (
        <div className="border-t border-amber-900/40 bg-stone-900 p-3" data-testid="wb-transform-toolbar">
          <div className="text-amber-200 text-xs font-semibold mb-2 capitalize">
            Editing · rot {Math.round(((draft.rotation_y * 180) / Math.PI) % 360)}° · scale {draft.scale.toFixed(1)}×
          </div>
          <div className="grid grid-cols-4 gap-2 mb-2">
            <button onClick={toggleMoveMode} className={`${bigBtn} ${moveMode ? 'bg-amber-600 text-white' : 'bg-stone-800 text-stone-200'}`} data-testid="wb-move-btn"><Move className="w-5 h-5" />Move</button>
            <button onClick={() => rotateSelected(-1)} className={`${bigBtn} bg-stone-800 text-stone-200`} data-testid="wb-rotate-left-btn"><RotateCcw className="w-5 h-5" />Rot −</button>
            <button onClick={() => rotateSelected(1)} className={`${bigBtn} bg-stone-800 text-stone-200`} data-testid="wb-rotate-right-btn"><RotateCw className="w-5 h-5" />Rot +</button>
            <button onClick={() => duplicateSelected()} className={`${bigBtn} bg-stone-800 text-stone-200`} data-testid="wb-duplicate-btn"><Copy className="w-5 h-5" />Dup</button>
            <button onClick={() => scaleSelected(-1)} className={`${bigBtn} bg-stone-800 text-stone-200`} data-testid="wb-scale-down-btn"><Minus className="w-5 h-5" />Size −</button>
            <button onClick={() => scaleSelected(1)} className={`${bigBtn} bg-stone-800 text-stone-200`} data-testid="wb-scale-up-btn"><Maximize2 className="w-5 h-5" />Size +</button>
            <button onClick={() => deleteSelected()} className={`${bigBtn} bg-red-900/60 text-red-200`} data-testid="wb-delete-btn"><Trash2 className="w-5 h-5" />Delete</button>
            <button onClick={() => cancelSelected()} className={`${bigBtn} bg-stone-800 text-stone-200`} data-testid="wb-cancel-btn"><X className="w-5 h-5" />Cancel</button>
          </div>
          <button onClick={() => saveSelected()} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold pointer-events-auto" data-testid="wb-save-btn">
            <Check className="w-5 h-5" />Save
          </button>
          {moveMode && <div className="mt-2 text-center text-amber-200 text-xs">Tap the ground to move this object</div>}
        </div>
      )}
    </div>
  );
}
